import type { WithId } from '@medplum/core';
import type { Age, Coding, FamilyMemberHistory, FamilyMemberHistoryCondition } from '@medplum/fhirtypes';
import { compareRelativeSlots, relativeSlot, type RelativeSlot } from './familyRelationship';
import { IDENTIFIER_SYSTEMS, SNOMED_SYSTEM } from './identifiers';

export type FamilyHistoryRecord = WithId<FamilyMemberHistory>;

/** One relative: the record shown for them plus every other resource describing the same person
 * (a "natural father" row from one site beside a "father" row from another). */
export type FamilyRelative = {
  /** Stable key: the slot for relations a person has one of, otherwise the record id. */
  key: string;
  slot: RelativeSlot;
  record: FamilyHistoryRecord;
  merged: FamilyHistoryRecord[];
};

/** One condition as recorded on one resource. */
export type FamilyHistoryConditionSource = {
  record: FamilyHistoryRecord;
  condition: FamilyMemberHistoryCondition;
};

export type FamilyHistoryCondition = {
  /** Shared by every entry describing the condition, across relatives: a coding when one exists,
   * otherwise the normalised label. */
  key: string;
  /** The SNOMED display when an entry carries one, otherwise the longest spelling a source used. */
  label: string;
  coding?: Coding;
};

/** One relative has one condition. Several entries (spelling variants, re-imports) collapse here. */
export type FamilyHistoryFact = {
  relative: FamilyRelative;
  condition: FamilyHistoryCondition;
  /** Earliest onset recorded across the merged entries. */
  onsetAge?: Age;
  contributedToDeath: boolean;
  sources: FamilyHistoryConditionSource[];
};

export type FamilyRelativeRow = { relative: FamilyRelative; conditions: FamilyHistoryFact[] };

export type FamilyConditionRow = { condition: FamilyHistoryCondition; facts: FamilyHistoryFact[] };

export type GroupedFamilyHistory = {
  /** Relatives with at least one condition, parents first, then siblings, children, grandparents,
   * aunts and uncles, others. Within a relative, conditions with an onset age first. */
  relatives: FamilyRelativeRow[];
  /** Relatives with nothing recorded, or only a "no known problems" placeholder: a documented negative. */
  negatives: FamilyRelative[];
  /** Conditions recorded against no particular relative ("family member", "other"). Kept out of
   * the condition pivot so they do not become singleton rows. */
  unattributed: FamilyRelativeRow | undefined;
  /** Pivot of `relatives`: one row per condition, rows with a first-degree relative first, then by
   * how many relatives are affected, then by label. */
  conditions: FamilyConditionRow[];
};

/**
 * Spellings that name one condition. Matched against the whole normalised label, never a
 * substring, so "type 2 diabetes with neuropathy" is not folded into "type 2 diabetes". Joining
 * only: the displayed label still comes from the source entries.
 */
export const FAMILY_CONDITION_ALIASES: Record<string, string[]> = {
  'alzheimers-disease': ['alzheimers disease', 'alzheimers', 'alzheimer disease', 'dementia alzheimer type'],
  'breast-cancer': ['breast cancer', 'breast ca', 'malignant neoplasm of breast', 'malignant tumor of breast'],
  'colon-cancer': [
    'colon cancer',
    'colon ca',
    'colorectal cancer',
    'cancer of colon',
    'malignant neoplasm of colon',
    'malignant tumor of colon',
    'bowel cancer',
  ],
  'coronary-artery-disease': [
    'coronary artery disease',
    'cad',
    'coronary heart disease',
    'coronary arteriosclerosis',
    'ischemic heart disease',
  ],
  'heart-failure': ['heart failure', 'congestive heart failure', 'chf'],
  hyperlipidemia: ['hyperlipidemia', 'high cholesterol', 'hypercholesterolemia', 'dyslipidemia'],
  hypertension: ['hypertension', 'high blood pressure', 'htn', 'essential hypertension'],
  'kidney-disease': ['kidney disease', 'renal disease', 'chronic kidney disease', 'ckd'],
  'lung-cancer': ['lung cancer', 'lung ca', 'malignant neoplasm of lung'],
  'macular-degeneration': ['macular degeneration', 'macular degen', 'age related macular degeneration'],
  'myocardial-infarction': ['myocardial infarction', 'heart attack'],
  neuropathy: ['neuropathy', 'peripheral neuropathy'],
  'pancreatic-cancer': ['pancreatic cancer', 'pancreas cancer', 'pancreatic ca', 'malignant neoplasm of pancreas'],
  'parkinsons-disease': ['parkinsons disease', 'parkinsons', 'parkinson disease'],
  'peripheral-arterial-disease': ['peripheral arterial disease', 'peripheral vascular disease', 'pad', 'pvd'],
  'prostate-cancer': ['prostate cancer', 'prostate ca', 'malignant neoplasm of prostate'],
  'rheumatoid-arthritis': ['rheumatoid arthritis', 'arthritis rheumatoid'],
  'skin-cancer': ['skin cancer', 'skin ca', 'malignant neoplasm of skin'],
  stroke: ['stroke', 'cva', 'cerebrovascular accident'],
  'type-1-diabetes': [
    'type 1 diabetes',
    'type i diabetes',
    'diabetes type 1',
    'diabetes type i',
    'diabetes mellitus type 1',
    'type 1 diabetes mellitus',
    'insulin dependent diabetes',
    'iddm',
  ],
  'type-2-diabetes': [
    'type 2 diabetes',
    'type ii diabetes',
    'diabetes type 2',
    'diabetes type ii',
    'diabetes mellitus type 2',
    'type 2 diabetes mellitus',
    'non insulin dependent diabetes',
    'niddm',
  ],
  'alcohol-use': ['alcohol', 'alcoholism', 'alcohol abuse', 'alcohol use disorder', 'alcohol dependence'],
  depression: ['depression', 'depressive disorder', 'major depressive disorder'],
};

/** Entries that record a documented negative rather than a condition. */
const NEGATIVE_PLACEHOLDER_CODES: ReadonlySet<string> = new Set(['160245001']);

const NEGATIVE_PLACEHOLDER_TEXT = [
  /^no known/,
  /^no significant/,
  /^no (family )?history/,
  /^no (medical )?(problems|conditions|illness)/,
  /^negative/,
  /^healthy$/,
  /^none$/,
  /^non ?contributory$/,
  /^unremarkable$/,
  /^no (data|information) available/,
  /^nkp$/,
];

/** Category words a source records beside the specific condition. "Cancer" is dropped when a
 * specific cancer is present; "other" whenever any real condition is. */
const CANCER_CATEGORY_LABELS: ReadonlySet<string> = new Set([
  'cancer',
  'ca',
  'cancer nos',
  'malignancy',
  'malignant neoplasm',
  'malignant neoplastic disease',
  'neoplasm',
  'tumor',
  'tumour',
]);

const OTHER_CATEGORY_LABELS: ReadonlySet<string> = new Set([
  'other',
  'others',
  'other condition',
  'other conditions',
  'unknown',
  'unspecified',
  'misc',
  'miscellaneous',
]);

const CANCER_WORDS =
  /\b(cancer|ca|carcinoma|melanoma|lymphoma|leukemia|leukaemia|neoplasm|sarcoma|myeloma|malignan\w*)\b/;

const TRAILING_QUALIFIER = /\s*\((disorder|finding|situation|event|morphologic abnormality)\)\s*$/i;

const collapseWhitespace = (value: string): string => value.replace(/\s+/g, ' ').trim();

/** A condition label reduced to what identifies it: case-folded, apostrophes closed up,
 * parentheticals and punctuation removed. "Alzheimer's Disease" becomes "alzheimers disease". */
export const normalizeConditionLabel = (value: string): string =>
  collapseWhitespace(
    value
      .toLowerCase()
      .replace(/\([^()]*\)/g, ' ')
      .replace(/['’]/g, '')
      .replace(/[^a-z0-9]+/g, ' '),
  );

const ALIAS_BY_LABEL: ReadonlyMap<string, string> = new Map(
  Object.entries(FAMILY_CONDITION_ALIASES).flatMap(([key, labels]) => labels.map((label) => [label, key] as const)),
);

/** Whether the resource was ingested from an outside source rather than authored in Liza. */
export const isImportedFamilyMemberHistory = (record: FamilyMemberHistory): boolean =>
  (record.identifier ?? []).some((id) => id.system === IDENTIFIER_SYSTEMS.EXTERNAL_ORIGINAL_ID);

export const familyRelativeRecords = (relative: FamilyRelative): FamilyHistoryRecord[] => [
  relative.record,
  ...relative.merged,
];

/** The names an entry carries: `code.text` and every coding display, as written. */
const conditionTexts = (condition: FamilyMemberHistoryCondition): string[] =>
  [condition.code?.text, ...(condition.code?.coding ?? []).map((c) => c.display)]
    .map((n) => collapseWhitespace((n ?? '').replace(TRAILING_QUALIFIER, '')))
    .filter(Boolean);

const isNegativePlaceholder = (condition: FamilyMemberHistoryCondition): boolean => {
  if ((condition.code?.coding ?? []).some((c) => c.code && NEGATIVE_PLACEHOLDER_CODES.has(c.code))) return true;
  const labels = conditionTexts(condition).map(normalizeConditionLabel);
  return labels.length > 0 && labels.every((label) => NEGATIVE_PLACEHOLDER_TEXT.some((p) => p.test(label)));
};

const ageInYears = (age: Age): number | undefined => {
  if (age.value === undefined) return undefined;
  switch (age.code ?? age.unit) {
    case 'mo':
    case 'month':
    case 'months':
      return age.value / 12;
    case 'wk':
    case 'week':
    case 'weeks':
      return age.value / 52;
    case 'd':
    case 'day':
    case 'days':
      return age.value / 365;
    default:
      return age.value;
  }
};

const earliestOnset = (sources: FamilyHistoryConditionSource[]): Age | undefined => {
  let earliest: Age | undefined;
  for (const { condition } of sources) {
    const years = condition.onsetAge ? ageInYears(condition.onsetAge) : undefined;
    if (years === undefined) continue;
    const current = earliest ? ageInYears(earliest) : undefined;
    if (current === undefined || years < current) earliest = condition.onsetAge;
  }
  return earliest;
};

type Clusters = {
  /** Registers an entry under its keys and returns its id; entries sharing any key join one cluster. */
  add: (keys: string[]) => number;
  /** The cluster an entry ended up in, once every entry is added. */
  root: (id: number) => number;
};

/** Union-find over condition entries so a SNOMED-coded row, a case variant and an alias all land
 * in one cluster, transitively, across every relative on the chart. */
const createClusters = (): Clusters => {
  const parent: number[] = [];
  const byKey = new Map<string, number>();

  const root = (id: number): number => {
    let current = id;
    while (parent[current] !== current) current = parent[current]!;
    return current;
  };

  const union = (a: number, b: number): void => {
    const ra = root(a);
    const rb = root(b);
    if (ra !== rb) parent[ra] = rb;
  };

  const add = (keys: string[]): number => {
    const id = parent.length;
    parent.push(id);
    for (const key of keys) {
      const existing = byKey.get(key);
      if (existing === undefined) byKey.set(key, id);
      else union(id, existing);
    }
    return id;
  };

  return { add, root };
};

const conditionKeys = (condition: FamilyMemberHistoryCondition): string[] => {
  const keys: string[] = [];
  for (const coding of condition.code?.coding ?? []) {
    if (coding.system && coding.code) keys.push(`code:${coding.system}|${coding.code}`);
  }
  for (const text of conditionTexts(condition)) {
    const label = normalizeConditionLabel(text);
    if (!label) continue;
    keys.push(`text:${label}`);
    keys.push(`tokens:${label.split(' ').sort().join(' ')}`);
    const alias = ALIAS_BY_LABEL.get(label);
    if (alias) keys.push(`alias:${alias}`);
  }
  return keys;
};

const snomedCoding = (condition: FamilyMemberHistoryCondition): Coding | undefined =>
  condition.code?.coding?.find((c) => c.system === SNOMED_SYSTEM && c.code);

const describeCluster = (entries: FamilyMemberHistoryCondition[]): FamilyHistoryCondition => {
  const coded = entries.map(snomedCoding).find((c) => c !== undefined);
  const displayed = entries
    .map(snomedCoding)
    .map((c) => c?.display && collapseWhitespace(c.display.replace(TRAILING_QUALIFIER, '')))
    .find((d): d is string => Boolean(d));
  const texts = entries.flatMap(conditionTexts);
  const longest = texts.reduce((best, text) => (text.length > best.length ? text : best), '');
  const label = displayed ?? longest;
  return {
    key: coded ? `${coded.system}|${coded.code}` : normalizeConditionLabel(label),
    label,
    coding: coded,
  };
};

const isCancerCategory = (sources: FamilyHistoryConditionSource[]): boolean =>
  sources
    .flatMap((s) => conditionTexts(s.condition))
    .map(normalizeConditionLabel)
    .every((label) => CANCER_CATEGORY_LABELS.has(label));

const isOtherCategory = (sources: FamilyHistoryConditionSource[]): boolean =>
  sources
    .flatMap((s) => conditionTexts(s.condition))
    .map(normalizeConditionLabel)
    .every((label) => OTHER_CATEGORY_LABELS.has(label));

const namesSpecificCancer = (condition: FamilyHistoryCondition): boolean =>
  CANCER_WORDS.test(normalizeConditionLabel(condition.label)) &&
  !CANCER_CATEGORY_LABELS.has(normalizeConditionLabel(condition.label));

/** Category entries a source records beside the specific condition are dropped when that specific
 * condition is present on the same relative; alone, they stay. */
const suppressCategories = (facts: FamilyHistoryFact[]): FamilyHistoryFact[] => {
  const hasSpecificCancer = facts.some((f) => namesSpecificCancer(f.condition));
  const withoutCancer = hasSpecificCancer ? facts.filter((f) => !isCancerCategory(f.sources)) : facts;
  const hasReal = withoutCancer.some((f) => !isOtherCategory(f.sources));
  return hasReal ? withoutCancer.filter((f) => !isOtherCategory(f.sources)) : withoutCancer;
};

const compareRecordsForDisplay = (a: FamilyHistoryRecord, b: FamilyHistoryRecord): number =>
  Number(isImportedFamilyMemberHistory(a)) - Number(isImportedFamilyMemberHistory(b)) ||
  (b.condition?.length ?? 0) - (a.condition?.length ?? 0) ||
  (b.date ?? '').localeCompare(a.date ?? '') ||
  a.id.localeCompare(b.id);

/** Resources sharing a slot a person has one of (father, mother, each specific grandparent, the
 * unattributed bucket) are one relative; every other slot is one relative per resource. */
const collectRelatives = (records: FamilyHistoryRecord[]): FamilyRelative[] => {
  const unique = new Map<string, FamilyHistoryRecord[]>();
  const relatives: FamilyRelative[] = [];
  for (const record of records) {
    const slot = relativeSlot(record);
    if (slot.unique) {
      const rows = unique.get(slot.key) ?? [];
      rows.push(record);
      unique.set(slot.key, rows);
    } else {
      relatives.push({ key: `${slot.key}:${record.id}`, slot, record, merged: [] });
    }
  }
  for (const [key, rows] of unique) {
    const [record, ...merged] = [...rows].sort(compareRecordsForDisplay);
    relatives.push({ key, slot: relativeSlot(record!), record: record!, merged });
  }
  return relatives;
};

const byOnsetThenLabel = (a: FamilyHistoryFact, b: FamilyHistoryFact): number => {
  const onsetA = a.onsetAge ? ageInYears(a.onsetAge) : undefined;
  const onsetB = b.onsetAge ? ageInYears(b.onsetAge) : undefined;
  if (onsetA !== undefined && onsetB !== undefined && onsetA !== onsetB) return onsetA - onsetB;
  if ((onsetA === undefined) !== (onsetB === undefined)) return onsetA === undefined ? 1 : -1;
  return a.condition.label.localeCompare(b.condition.label) || a.condition.key.localeCompare(b.condition.key);
};

const compareRelativeRows = (a: FamilyRelativeRow, b: FamilyRelativeRow): number =>
  compareRelativeSlots(a.relative.slot, b.relative.slot) ||
  b.conditions.length - a.conditions.length ||
  a.relative.record.id.localeCompare(b.relative.record.id);

const isFirstDegree = (row: FamilyConditionRow): boolean => row.facts.some((f) => f.relative.slot.degree === 1);

const compareConditionRows = (a: FamilyConditionRow, b: FamilyConditionRow): number =>
  Number(isFirstDegree(b)) - Number(isFirstDegree(a)) ||
  b.facts.length - a.facts.length ||
  a.condition.label.localeCompare(b.condition.label) ||
  a.condition.key.localeCompare(b.condition.key);

type Entry = { relative: FamilyRelative; source: FamilyHistoryConditionSource; cluster: number };

/** Every real condition entry on the chart, registered with the clusters it joins. Negative
 * placeholders and entries with nothing to key on are left out. */
const collectEntries = (relatives: FamilyRelative[], clusters: Clusters): Entry[] => {
  const entries: Entry[] = [];
  for (const relative of relatives) {
    for (const record of familyRelativeRecords(relative)) {
      for (const condition of record.condition ?? []) {
        if (isNegativePlaceholder(condition)) continue;
        const keys = conditionKeys(condition);
        if (keys.length === 0) continue;
        entries.push({ relative, source: { record, condition }, cluster: clusters.add(keys) });
      }
    }
  }
  return entries;
};

/** One fact per relative per cluster, before suppression and ordering. */
const collectFacts = (entries: Entry[], clusters: Clusters): Map<string, FamilyHistoryFact[]> => {
  const clusterEntries = new Map<number, FamilyMemberHistoryCondition[]>();
  for (const entry of entries) {
    const root = clusters.root(entry.cluster);
    clusterEntries.set(root, [...(clusterEntries.get(root) ?? []), entry.source.condition]);
  }
  const conditionsByCluster = new Map<number, FamilyHistoryCondition>();
  for (const [root, conditions] of clusterEntries) conditionsByCluster.set(root, describeCluster(conditions));

  const factsByRelative = new Map<string, Map<number, FamilyHistoryFact>>();
  for (const entry of entries) {
    const root = clusters.root(entry.cluster);
    const facts = factsByRelative.get(entry.relative.key) ?? new Map<number, FamilyHistoryFact>();
    const fact = facts.get(root) ?? {
      relative: entry.relative,
      condition: conditionsByCluster.get(root)!,
      contributedToDeath: false,
      sources: [],
    };
    fact.sources.push(entry.source);
    fact.contributedToDeath ||= entry.source.condition.contributedToDeath === true;
    facts.set(root, fact);
    factsByRelative.set(entry.relative.key, facts);
  }
  return new Map([...factsByRelative].map(([key, facts]) => [key, [...facts.values()]]));
};

/** The relative's facts after category suppression, with the earliest onset on each, ordered. */
const curatedFacts = (facts: FamilyHistoryFact[]): FamilyHistoryFact[] =>
  suppressCategories(facts)
    .map((fact) => ({ ...fact, onsetAge: earliestOnset(fact.sources) }))
    .sort(byOnsetThenLabel);

/** One row per condition across the given relative rows, relatives in the order the rows have. */
const pivotByCondition = (rows: FamilyRelativeRow[]): FamilyConditionRow[] => {
  const position = new Map(rows.map((row, index) => [row.relative.key, index]));
  const byCondition = new Map<string, FamilyConditionRow>();
  for (const row of rows) {
    for (const fact of row.conditions) {
      const existing = byCondition.get(fact.condition.key) ?? { condition: fact.condition, facts: [] };
      existing.facts.push(fact);
      byCondition.set(fact.condition.key, existing);
    }
  }
  const conditions = [...byCondition.values()];
  for (const row of conditions) {
    row.facts.sort((a, b) => position.get(a.relative.key)! - position.get(b.relative.key)!);
  }
  return conditions.sort(compareConditionRows);
};

/**
 * The patient's family history as facts (one relative has one condition) with the noise imported
 * rows carry removed, and both ways of reading it: per relative and per condition. Pure and
 * synchronous; call it on the whole list, since grouping a page gives wrong merges and counts.
 */
export const groupFamilyHistoryRecords = (records: FamilyHistoryRecord[]): GroupedFamilyHistory => {
  const relatives = collectRelatives(records);
  const clusters = createClusters();
  const factsByRelative = collectFacts(collectEntries(relatives, clusters), clusters);

  const rows: FamilyRelativeRow[] = [];
  const negatives: FamilyRelative[] = [];
  let unattributed: FamilyRelativeRow | undefined;
  for (const relative of relatives) {
    const facts = curatedFacts(factsByRelative.get(relative.key) ?? []);
    if (facts.length === 0) {
      negatives.push(relative);
    } else if (relative.slot.key === 'unspecified') {
      unattributed = { relative, conditions: facts };
    } else {
      rows.push({ relative, conditions: facts });
    }
  }
  rows.sort(compareRelativeRows);
  negatives.sort((a, b) => compareRelativeSlots(a.slot, b.slot) || a.record.id.localeCompare(b.record.id));

  return { relatives: rows, negatives, unattributed, conditions: pivotByCondition(rows) };
};
